import { BookOpen, HelpCircle } from "lucide-react";
import type React from "react";
import cashforecasterIcon from "@/assets/cashforecaster.svg";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useApp } from "../../contexts/AppContext";
import { useTour } from "../../contexts/TourContext";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { DEMO_USERS } from "../../contexts/UsersContext";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../ui/tooltip";

interface HeaderProps {
	pageTitle?: string;
	onUserChange?: (userId: string) => void;
	onHelpOpen?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
	pageTitle,
	onUserChange,
	onHelpOpen,
}) => {
	const { currentUser, setCurrentUser, mode, setMode } = useApp();
	const { setIsMenuOpen } = useTour();
	const { getUserPreferences } = useUserPreferences();
	const currentUserPrefs = getUserPreferences(currentUser.id);

	// Filter out the 'regional' user (Marcus Patel) — hidden R1 feature
	const availableUsers = DEMO_USERS.filter(
		(u) => u.forecasterRole !== "Regional Manager",
	);

	return (
		<header className="border-b bg-background sticky top-0 z-50">
			<div className="flex h-16 items-center px-4 gap-4">
				{/* Logo */}
				<div className="flex items-center gap-2 font-semibold text-lg">
					<img
						src={cashforecasterIcon}
						alt="Cash Forecaster"
						className="h-6 w-6"
					/>
					<span>{pageTitle || "Cash Forecaster"}</span>
				</div>

				{/* Spacer */}
				<div className="flex-1" />

				{/* Help Button */}
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="outline"
								size="sm"
								onClick={() => onHelpOpen?.()}
								className="gap-1.5"
							>
								<BookOpen className="h-4 w-4" />
								Help
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">
							Get help with this page
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>

				{/* Tour Menu Button */}
				<Button
					variant="ghost"
					size="icon"
					onClick={() => setIsMenuOpen(true)}
					title="Product Tours & Help"
				>
					<HelpCircle className="h-5 w-5" />
				</Button>

				{/* User Switcher - Only visible in Demo Mode */}
				{mode === "demo" && (
					<div className="flex items-center gap-2">
						<Label
							htmlFor="user-select"
							className="text-sm text-muted-foreground"
						>
							Viewing as:
						</Label>
						<Select
							value={currentUser.id}
							onValueChange={(userId) => {
								const user = DEMO_USERS.find((u) => u.id === userId);
								if (user) {
									setCurrentUser(user);
									onUserChange?.(userId);
								}
							}}
						>
							<SelectTrigger id="user-select" className="w-[220px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{availableUsers.map((u) => (
									<SelectItem key={u.id} value={u.id}>
										{u.name} ({u.forecasterRole})
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}

				{/* Mode Toggle - Always visible */}
				<div className="flex items-center gap-2 border-l pl-4">
					<Switch
						id="mode-toggle"
						checked={mode === "live"}
						onCheckedChange={(checked) => setMode(checked ? "live" : "demo")}
					/>
					<Label htmlFor="mode-toggle" className="text-sm">
						{mode === "live" ? (
							<span className="flex items-center gap-1">
								<span className="h-2 w-2 rounded-full bg-green-500" />
								Live Mode
							</span>
						) : (
							<span className="flex items-center gap-1">
								<span className="h-2 w-2 rounded-full bg-orange-500" />
								Demo Mode
							</span>
						)}
					</Label>
				</div>

				{/* Current user avatar */}
				<div className="border-l pl-4">
					<UserAvatar
						name={currentUser.name}
						avatarUrl={currentUserPrefs.avatarUrl}
						size="sm"
					/>
				</div>
			</div>
		</header>
	);
};
